import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../app/hooks'
import { useLocationByIdQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { useGetCustomerByIdQuery } from '../../../app/redux-fetch/apiQuery'
import SpinnerModalBlock from '../../../components/Spinner/SpinnerModalBlock'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ICustomer, ILoggedInUser } from '../../types'
import CustomerForm from './CustomerForm'

type EditCustomerProps = {
  customerId: number
  isDisabled?: boolean
}

const EditCustomer = ({ customerId, isDisabled = false }: EditCustomerProps) => {
  const { tenantId } = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)

  const {
    data: customerData,
    isSuccess: customerLoaded,
    isLoading: isCustomerFetching,
  } = useGetCustomerByIdQuery(
    {
      tenantId,
      customerId,
    },
    { refetchOnMountOrArgChange: true }
  )

  const {
    data: locationFallback,
    isSuccess: locationFallbackLoaded,
    isLoading: isLocationFallbackFetching,
  } = useLocationByIdQuery(
    {
      tenantId,
      locationId: customerData?.locationId ?? 0,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: isDisabled || !customerData?.locationId || !!customerData?.location,
    }
  )

  const [updatedCustomer, setUpdatedCustomer] = useState<ICustomer>()

  useEffect(() => {
    if (isCustomerFetching || isLocationFallbackFetching) {
      setUpdatedCustomer(undefined)
    }
  }, [isCustomerFetching, isLocationFallbackFetching])

  useEffect(() => {
    if (!customerLoaded || !customerData || isCustomerFetching) return

    const location = customerData.location ?? locationFallback
    if (!location && !isDisabled) return

    const updatedCustomerWithLocation = {
      ...customerData,
      ...(location ?? {}),
      name: customerData.name,
      locationName: location?.name,
      locationId: location?.id ?? customerData.locationId,
      // customerData.currencyId (spread above) is the customer's own tenant-catalogue currency; the
      // logged-in tenant's currencyId lives in the Tenant-0 platform-catalogue id-space and must not
      // be injected here — the two id-spaces don't line up.
      id: customerData.id,
      location: location,
      defaultTransportModeId: location?.defaultTransportModeId ?? undefined,
      validFrom: customerData.validFrom,
    }
    setUpdatedCustomer(updatedCustomerWithLocation as ICustomer)
  }, [
    isLocationFallbackFetching,
    isCustomerFetching,
    customerLoaded,
    locationFallbackLoaded,
    customerData,
    locationFallback,
    isDisabled,
  ])

  if (!customerLoaded || !updatedCustomer) return <SpinnerModalBlock />
  return <CustomerForm customer={updatedCustomer as ICustomer} isDisabled={isDisabled} />
}

export default EditCustomer
